import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { useUpdateService } from "@/services/query/services.query"
import { useForm, useFieldArray } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useToast } from "@/components/ui/use-toast"
import PropTypes from "prop-types"
import { Loader2, Plus, X } from "lucide-react"
import { useEffect } from "react"

const formSchema = z.object({
  title: z.string().min(2, "Title must be at least 2 characters"),
  descriptions: z.array(
    z.object({
      key: z.string().min(1, "Key is required"),
      value: z.string().min(10, "Value must be at least 10 characters"),
    })
  ),
  meta: z.object({}).default({}),
})

export function EditServiceDialog({ service, open, onOpenChange }) {
  const { toast } = useToast()
  const updateServiceMutation = useUpdateService()

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      descriptions: [{ key: "", value: "" }],
      meta: {},
    },
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "descriptions",
  })

  useEffect(() => {
    if (service && open) {
      const descriptions = Object.entries(service.description || {}).map(
        ([key, value]) => ({
          key,
          value,
        })
      )

      form.reset({
        title: service.title,
        descriptions:
          descriptions.length > 0 ? descriptions : [{ key: "", value: "" }],
        meta: service.meta || {},
      })
    }
  }, [service, open, form])

  const onSubmit = async (values) => {
    try {
      const description = values.descriptions.reduce((acc, { key, value }) => {
        acc[key] = value
        return acc
      }, {})

      await updateServiceMutation.mutateAsync({
        id: service.id,
        data: {
          title: values.title,
          description,
          meta: values.meta,
        },
      })

      toast({
        title: "Success",
        description: "Service updated successfully",
        className: "bg-green-50 border-green-500",
      })
      onOpenChange(false)
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update service",
        variant: "destructive",
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Edit Service
          </DialogTitle>
          <DialogDescription>
            Modify the service information below.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter service title"
                      {...field}
                      className="focus-visible:ring-purple-500"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <FormLabel className="text-lg font-semibold">
                  Description Sections
                </FormLabel>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append({ key: "", value: "" })}
                  className="flex items-center gap-2 border-purple-500 text-purple-500 hover:bg-purple-50"
                >
                  <Plus className="h-4 w-4" /> Add Section
                </Button>
              </div>

              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="grid grid-cols-12 gap-4 items-start p-4 rounded-lg border border-gray-200 bg-gray-50"
                  >
                    <div className="col-span-3">
                      <FormField
                        control={form.control}
                        name={`descriptions.${index}.key`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Section Title
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g., DIAGNOSIS"
                                {...field}
                                className="focus-visible:ring-purple-500 bg-white"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="col-span-8">
                      <FormField
                        control={form.control}
                        name={`descriptions.${index}.value`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Content
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Enter detailed description"
                                {...field}
                                className="min-h-[100px] focus-visible:ring-purple-500 bg-white"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="col-span-1 flex justify-end pt-8">
                      {fields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => remove(index)}
                          className="hover:bg-red-100 hover:text-red-500 rounded-full h-8 w-8"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter className="gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateServiceMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateServiceMutation.isPending}
                className={`min-w-[120px] relative ${
                  updateServiceMutation.isPending
                    ? "bg-purple-400"
                    : "bg-purple-500 hover:bg-purple-600"
                }`}
              >
                {updateServiceMutation.isPending ? (
                  <div className="flex items-center">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </div>
                ) : (
                  "Update Service"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

EditServiceDialog.propTypes = {
  service: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.object,
    meta: PropTypes.object,
  }),
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
}
